import { useState, type FormEvent } from "react";
import { DEFAULT_GATEWAY_URL } from "../openclaw/constants";
import { normalizeGatewayUrl } from "../openclaw/connection";
import { useT } from "../lib/useT";
import { useStore } from "../state/store";

/**
 * First-run pairing.
 *
 * The user supplies the two things only they can know — where their Gateway is
 * and the token that lets a new device in. Everything else (the Ed25519 device
 * key, the issued device token) is created and kept locally, which is what makes
 * every later launch skip this screen entirely.
 */
export function SetupScreen() {
  const connect = useStore((state) => state.connect);
  const t = useT();
  const [url, setUrl] = useState(DEFAULT_GATEWAY_URL);
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    let normalized: string;
    try {
      normalized = normalizeGatewayUrl(url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("setup.badUrl"));
      return;
    }
    setBusy(true);
    try {
      await connect({ url: normalized, token });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("setup.failed"));
      setBusy(false);
    }
  }

  return (
    <div className="setup">
      <div className="setup-card">
        <img className="setup-logo" src="./icons/icon-192.png" alt="" width={68} height={68} />
        <h1>cllw</h1>
        <p className="setup-lead">
          {t("setup.lead")}
        </p>

        <form onSubmit={onSubmit}>
          {error ? <p className="form-error">{error}</p> : null}

          <div className="field">
            <label htmlFor="gateway-url">{t("setup.urlLabel")}</label>
            <input
              id="gateway-url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder={DEFAULT_GATEWAY_URL}
              autoComplete="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="url"
              required
            />
            <p
              className="field-hint"
              dangerouslySetInnerHTML={{ __html: t("setup.urlHint") }}
            />
          </div>

          <div className="field">
            <label htmlFor="gateway-token">{t("setup.tokenLabel")}</label>
            <input
              id="gateway-token"
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder={t("setup.tokenPlaceholder")}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <p className="field-hint">{t("setup.tokenHint")}</p>
          </div>

          <button className="button-primary" type="submit" disabled={busy}>
            {busy ? t("setup.submitting") : t("setup.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
